'use client';
import styles from './ContactSection.module.css';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, useForm } from 'react-hook-form';
import RecaptchaScript from '../Recaptcha/Recaptcha';
import { useEffect } from 'react';
import { Animate } from '../shared/Animation/Animate';
import { Mail, MapPin, Phone } from 'lucide-react';

const contactUsFormDataSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(255),
  email: z.string().email('Please enter a valid email address'),
  message: z
    .string()
    .min(10, 'Message must be at least 10 characters')
    .max(1000),
  recaptchaToken: z.string(),
});

const ContactSection: React.FC = () => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(contactUsFormDataSchema),
    defaultValues: {
      name: '',
      email: '',
      message: '',
      recaptchaToken: '',
    },
  });

  useEffect(() => {
    // Define the reCAPTCHA callback functions and attach them to the window object
    window.onRecaptchaSuccess = (token: string) => {
      setValue('recaptchaToken', token);
    };
    window.onRecaptchaExpired = () => {
      setValue('recaptchaToken', '');
    };
    return () => {
      // Remove the script and clean up the global functions
      delete window.onRecaptchaSuccess;
      delete window.onRecaptchaExpired;
    };
  }, [setValue]);

  return (
    <div className={styles.contactUsSection}>
      <RecaptchaScript />
      <div className={styles.contactWrapper}>
        <Animate animationName="slideRight" delay="100">
          <div className={styles.contactInfo}>
            <div className={styles.contactInfoItem}>
              <div className={styles.contactIconWrapper}>
                <Mail className={styles.contactIcon} />
              </div>
              <div>
                <h3 className={styles.contactInfoTitle}>Email</h3>
                <p className={styles.contactInfoText}>contact@JimGym.com</p>
                <p className={styles.contactInfoText}>support@JimGym.com</p>
              </div>
            </div>

            <div className={styles.contactInfoItem}>
              <div className={styles.contactIconWrapper}>
                <Phone className={styles.contactIcon} />
              </div>
              <div>
                <h3 className={styles.contactInfoTitle}>Phone</h3>
                <p className={styles.contactInfoText}>+1 (555) 123-4567</p>
                <p className={styles.contactInfoText}>Mon-Fri: 9am - 5pm EST</p>
              </div>
            </div>

            <div className={styles.contactInfoItem}>
              <div className={styles.contactIconWrapper}>
                <MapPin className={styles.contactIcon} />
              </div>
              <div>
                <h3 className={styles.contactInfoTitle}>Office</h3>
                <p className={styles.contactInfoText}>123 Tech Boulevard</p>
                <p className={styles.contactInfoText}>
                  San Francisco, CA 94107
                </p>
              </div>
            </div>
          </div>
        </Animate>

        <Animate animationName="slideLeft" delay="200">
          <div className={styles.contactForm}>
            <h3 className={styles.formTitle}>Send us a message</h3>
            <Form
              action={`/email`}
              method="post"
              onSuccess={() => {
                reset();
                window.grecaptcha.reset();
              }}
              onError={() => alert('Failed to send message. Please try again.')}
              control={control}
              encType="application/json"
              className={styles.form}
            >
              <div className={styles.formGroup}>
                <input
                  id="name"
                  className={styles.input}
                  placeholder="Your Name"
                  autoComplete="name"
                  {...register('name')}
                />
                {errors.name?.message && (
                  <span className={styles.error}>{errors.name?.message}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <input
                  id="email"
                  className={styles.input}
                  placeholder="Your Email"
                  autoComplete="email"
                  {...register('email')}
                />
                {errors.email?.message && (
                  <span className={styles.error}>{errors.email?.message}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <textarea
                  id="message"
                  className={styles.textarea}
                  placeholder="Your message"
                  {...register('message')}
                ></textarea>
                {errors.message?.message && (
                  <span className={styles.error}>
                    {errors.message?.message}
                  </span>
                )}
              </div>

              <div
                className="g-recaptcha"
                data-theme="dark"
                data-sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                data-callback="onRecaptchaSuccess"
                data-expired-callback="onRecaptchaExpired"
                data-action="CONTACT_US"
              ></div>
              <input
                type="hidden"
                {...register('recaptchaToken', { required: true })}
              />

              <button className={styles.submitButton} type="submit">
                Send Message
              </button>
            </Form>
          </div>
        </Animate>
      </div>

      <Animate animationName="slideUp" delay="300">
        <div className={styles.mapContainer}>
          <iframe
            title="JimGym Office Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0951475076775!2d-122.41384492313716!3d37.78089311578323!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085807ded297e89%3A0xcfd61a4390d9c5!2sSan%20Francisco%2C%20CA%2094107!5e0!3m2!1sen!2sus!4v1684994515629!5m2!1sen!2sus"
            className={styles.iframe}
            allowFullScreen
            loading="eager"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
          <div className={styles.mapOverlay}>
            <div className={styles.mapPin}></div>
          </div>
        </div>
      </Animate>
    </div>
  );
};

export default ContactSection;
